/**
 * CustomerAuthPages — Login, Set-Password (and reset via same token), Forgot.
 * Iter 78 (Feb 2026). All routes under `/customer/*` with dark Powersure-stijl.
 */
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Mail, Eye, EyeOff, Loader2, ArrowLeft, ShieldCheck, CheckCircle2, AlertCircle } from 'lucide-react';
import { customerLogin, customerSetPassword, customerForgot, customerApi } from '@/hooks/useCustomerAuth';

const SHELL =
  'min-h-screen flex items-center justify-center px-4 py-10 bg-[#0a1410] text-white';
const CARD =
  'w-full max-w-md rounded-2xl border border-white/10 p-6 sm:p-8 shadow-2xl ' +
  'bg-[radial-gradient(120%_80%_at_100%_0%,rgba(16,185,129,0.18)_0%,rgba(16,185,129,0)_55%),linear-gradient(180deg,#0e1b16_0%,#0a1410_100%)]';

function Field({ label, type, value, onChange, placeholder, icon: Icon, testId, autoComplete, error, rightSlot, name }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] uppercase tracking-wider text-white/55 font-medium mb-1.5">{label}</label>
      <div className={`flex items-center gap-2 px-3 py-2.5 rounded-md border transition-colors ${error ? 'border-rose-400/60 bg-rose-500/5' : 'border-white/15 bg-white/5 focus-within:border-emerald-400/50'}`}>
        {Icon ? <Icon className="w-4 h-4 text-white/55" /> : null}
        <input
          type={type}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-white/35 text-white"
          data-testid={testId}
        />
        {rightSlot}
      </div>
      {error && <p className="text-[12px] text-rose-300 mt-1.5" data-testid={`${testId}-error`}>{error}</p>}
    </div>
  );
}

function Page({ title, subtitle, children, testId }) {
  return (
    <div className={SHELL} data-testid={testId}>
      <div className={CARD}>
        <Link to="/" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-emerald-300 mb-4">
          <ArrowLeft className="w-3 h-3" /> Terug naar homepage
        </Link>
        <h1 className="text-xl sm:text-2xl font-semibold" style={{ fontFamily: 'Outfit' }}>{title}</h1>
        {subtitle && <p className="text-sm text-white/65 mt-1.5">{subtitle}</p>}
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

/* ----------------------- Login ----------------------- */
export function CustomerLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e?.preventDefault();
    setErr(''); setBusy(true);
    try {
      await customerLogin(email.trim(), pw);
      nav('/app', { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Inloggen mislukt.');
    } finally { setBusy(false); }
  };

  return (
    <Page title="Inloggen op Mijn Powersure" subtitle="Vul uw e-mailadres en wachtwoord in." testId="customer-login-page">
      <form onSubmit={submit}>
        <Field label="E-mail" type="email" value={email} onChange={setEmail} icon={Mail} placeholder="naam@voorbeeld.nl" testId="customer-login-email" autoComplete="email" name="email" />
        <Field
          label="Wachtwoord" type={showPw ? 'text' : 'password'} value={pw} onChange={setPw} icon={Lock}
          placeholder="••••••••" testId="customer-login-password" autoComplete="current-password" name="password"
          rightSlot={
            <button type="button" onClick={() => setShowPw((v) => !v)} className="text-white/55 hover:text-emerald-300" aria-label="Toon wachtwoord">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />
        {err && <p className="text-[12px] text-rose-300 -mt-1 mb-2" data-testid="customer-login-error">{err}</p>}
        <button type="submit" disabled={busy} className="w-full mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm disabled:opacity-60" data-testid="customer-login-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? 'Bezig…' : 'Inloggen'}
        </button>
        <div className="mt-4 flex items-center justify-between text-[12px]">
          <Link to="/customer/forgot-password" className="text-emerald-300 hover:text-emerald-200" data-testid="customer-login-forgot-link">Wachtwoord vergeten?</Link>
          <Link to="/" className="text-white/45 hover:text-white/75">Naar Powersure</Link>
        </div>
        <div className="mt-3 pt-3 border-t border-white/10 text-[12px] text-white/70">
          Nog geen wachtwoord? Heeft u betaald maar geen mail ontvangen?{' '}
          <Link to="/customer/resend-access" className="text-emerald-300 hover:text-emerald-200 underline" data-testid="customer-login-resend-access-link">Stuur de toegang opnieuw</Link>.
        </div>
      </form>
    </Page>
  );
}

/* ----------------------- Set Password (from token) ----------------------- */
export function CustomerSetPasswordPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get('token') || '';
  const [status, setStatus] = useState({ loading: true, valid: false, reason: '', emailMasked: '', isReset: false });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setStatus({ loading: false, valid: false, reason: 'missing_token' }); return; }
      try {
        const res = await customerApi.get(`/customer/auth/set-password-status?token=${encodeURIComponent(token)}`);
        if (!cancelled) setStatus({ loading: false, valid: !!res.data?.valid, reason: res.data?.reason || '', emailMasked: res.data?.email_masked || '', isReset: !!res.data?.is_reset });
      } catch {
        if (!cancelled) setStatus({ loading: false, valid: false, reason: 'network' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e?.preventDefault();
    setErr('');
    if (pw.length < 10) { setErr('Minimaal 10 tekens.'); return; }
    if (pw !== pw2) { setErr('Wachtwoorden komen niet overeen.'); return; }
    setBusy(true);
    try {
      await customerSetPassword(token, pw);
      setDone(true);
      setTimeout(() => nav('/app', { replace: true }), 1200);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Wachtwoord instellen mislukt.');
    } finally { setBusy(false); }
  };

  if (status.loading) {
    return <Page title="Even controleren…" testId="customer-setpw-page"><Loader2 className="w-5 h-5 animate-spin text-emerald-300" /></Page>;
  }
  if (!status.valid) {
    return (
      <Page title="Link is ongeldig of verlopen" subtitle="Vraag een nieuwe link aan of log in als u al een wachtwoord heeft." testId="customer-setpw-page">
        <div className="rounded-md bg-rose-500/10 border border-rose-400/30 px-3 py-2 text-sm text-rose-200 mb-4 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> Reden: {status.reason || 'invalid'}
        </div>
        <div className="flex flex-col gap-2">
          <Link to="/customer/login" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm">Naar Inloggen</Link>
          <Link to="/customer/forgot-password" className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-md border border-white/15 bg-white/5 text-white/85 text-sm">Wachtwoord vergeten</Link>
        </div>
      </Page>
    );
  }
  return (
    <Page
      title={status.isReset ? 'Wachtwoord opnieuw instellen' : 'Maak uw Mijn Powersure-account aan'}
      subtitle={`Voor account: ${status.emailMasked}. Kies een sterk wachtwoord (minimaal 10 tekens, letter + cijfer).`}
      testId="customer-setpw-page"
    >
      <form onSubmit={submit}>
        <Field
          label="Nieuw wachtwoord" type={showPw ? 'text' : 'password'} value={pw} onChange={setPw} icon={Lock}
          placeholder="Minimaal 10 tekens" testId="customer-setpw-password" autoComplete="new-password" name="new-password"
          rightSlot={
            <button type="button" onClick={() => setShowPw((v) => !v)} className="text-white/55 hover:text-emerald-300" aria-label="Toon wachtwoord">
              {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          }
        />
        <Field label="Bevestig wachtwoord" type={showPw ? 'text' : 'password'} value={pw2} onChange={setPw2} icon={ShieldCheck} placeholder="Herhaal wachtwoord" testId="customer-setpw-password2" autoComplete="new-password" name="confirm-password" />
        {err && <p className="text-[12px] text-rose-300 -mt-1 mb-2" data-testid="customer-setpw-error">{err}</p>}
        {done ? (
          <p className="text-emerald-300 text-sm inline-flex items-center gap-1.5" data-testid="customer-setpw-success"><CheckCircle2 className="w-4 h-4" /> Wachtwoord ingesteld — u wordt doorgestuurd…</p>
        ) : (
          <button type="submit" disabled={busy} className="w-full mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm disabled:opacity-60" data-testid="customer-setpw-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? 'Bezig…' : 'Wachtwoord instellen'}
          </button>
        )}
      </form>
    </Page>
  );
}

/* ----------------------- Resend Access (Blok 84) ----------------------- */
export function CustomerResendAccessPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await customerApi.post('/customer/account/resend-access', { email: email.trim() });
    } catch { /* always neutral — ignore */ }
    finally { setBusy(false); setDone(true); }
  };

  return (
    <Page
      title="Geen accountmail ontvangen?"
      subtitle="Vul het e-mailadres in waarmee u heeft betaald. Dan sturen we de toegang opnieuw als uw betaling of abonnement bekend is."
      testId="customer-resend-access-page"
    >
      {done ? (
        <div data-testid="customer-resend-access-done" className="text-sm text-emerald-300 inline-flex items-start gap-1.5">
          <CheckCircle2 className="w-4 h-4 mt-0.5" />
          <span>Als dit e-mailadres bekend is, ontvangt u binnen enkele minuten een mail. Controleer ook uw spam-/promotie-tab.</span>
        </div>
      ) : (
        <form onSubmit={submit}>
          <Field label="E-mail" type="email" value={email} onChange={setEmail} icon={Mail} placeholder="naam@voorbeeld.nl" testId="customer-resend-access-email" autoComplete="email" name="email" />
          <button
            type="submit"
            disabled={busy || !email}
            className="w-full mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm disabled:opacity-60"
            data-testid="customer-resend-access-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? 'Bezig…' : 'Stuur de toegang opnieuw'}
          </button>
          <p className="text-[11px] text-white/55 mt-3 leading-relaxed">
            Geen marketing — alleen een transactionele toegangsmail naar het opgegeven adres.
            We bevestigen niet of het adres bekend is om uw privacy te beschermen.
          </p>
        </form>
      )}
      <div className="mt-4 flex items-center gap-3 text-[12px]">
        <Link to="/customer/login" className="text-emerald-300 hover:text-emerald-200" data-testid="customer-resend-access-login-link">← Terug naar inloggen</Link>
        <span className="text-white/30">·</span>
        <Link to="/customer/forgot-password" className="text-white/65 hover:text-white/85" data-testid="customer-resend-access-forgot-link">Wachtwoord vergeten</Link>
      </div>
    </Page>
  );
}
export function CustomerForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault(); setBusy(true);
    try { await customerForgot(email.trim()); } catch { /* ignore */ }
    finally { setBusy(false); setDone(true); }
  };

  return (
    <Page title="Wachtwoord vergeten?" subtitle="We sturen u een herstellink als er een account is op dit e-mailadres." testId="customer-forgot-page">
      {done ? (
        <div data-testid="customer-forgot-done" className="text-sm text-emerald-300 inline-flex items-center gap-1.5">
          <CheckCircle2 className="w-4 h-4" /> Als er een account bestaat, ontvangt u binnen enkele minuten een mail met een herstellink.
        </div>
      ) : (
        <form onSubmit={submit}>
          <Field label="E-mail" type="email" value={email} onChange={setEmail} icon={Mail} placeholder="naam@voorbeeld.nl" testId="customer-forgot-email" autoComplete="email" name="email" />
          <button type="submit" disabled={busy} className="w-full mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-3 rounded-md bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-sm disabled:opacity-60" data-testid="customer-forgot-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{busy ? 'Bezig…' : 'Stuur herstellink'}
          </button>
        </form>
      )}
      <div className="mt-4 text-[12px]">
        <Link to="/customer/login" className="text-emerald-300 hover:text-emerald-200" data-testid="customer-forgot-login-link">← Terug naar inloggen</Link>
      </div>
    </Page>
  );
}
